import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';

const API = (path) =>
  fetch(path, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function OwnerClientDocuments() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [search, setSearch] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filterCompany) params.set('company_id', filterCompany);
    API(`/api/owner/client-documents?${params}`)
      .then(r => r.json())
      .then(d => {
        const rows = d.deals || [];
        setDeals(rows);
        const uniqueCompanies = [...new Map(rows.map(r => [r.company_id, { id: r.company_id, name: r.company_name }])).values()];
        setCompanies(uniqueCompanies);
        setLoading(false);
      })
      .catch(() => { setError('Failed to load'); setLoading(false); });
  }, [filterCompany]);

  const filtered = deals.filter(d =>
    !search ||
    (d.deal_ref || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.client_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.broker_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const progressPct = (d) => {
    const total = parseInt(d.total_docs || 0);
    const approved = parseInt(d.approved_docs || 0);
    return total > 0 ? Math.round((approved / total) * 100) : 0;
  };

  const progressColor = (pct) => pct === 100 ? '#10b981' : pct >= 50 ? '#3b82f6' : '#f59e0b';

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>Client Document Progress</h1>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>Document collection status for all active client portal invites</p>

        {error && <div style={alertRed}>{error}</div>}

        {/* Summary */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
          {[
            { label: 'Total Deals', value: filtered.length, color: '#3b82f6' },
            { label: 'Complete', value: filtered.filter(d => progressPct(d) === 100).length, color: '#10b981' },
            { label: 'In Progress', value: filtered.filter(d => { const p = progressPct(d); return p > 0 && p < 100; }).length, color: '#3b82f6' },
            { label: 'Not Started', value: filtered.filter(d => progressPct(d) === 0).length, color: '#9ca3af' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', borderRadius: '8px', padding: '16px 20px', borderLeft: `4px solid ${s.color}`, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', flex: 1 }}>
              <div style={{ fontSize: '22px', fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
          <input
            placeholder="Search deal, client or broker…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={inputStyle}
          />
          <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} style={inputStyle}>
            <option value="">All Companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading…</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Deal Ref', 'Client', 'Company', 'Broker', 'Portal Status', 'Documents', 'Progress'].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>No deals with client portal invites</td></tr>
                ) : filtered.map(d => {
                  const pct = progressPct(d);
                  const color = progressColor(pct);
                  return (
                    <tr key={d.deal_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={td}><span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{d.deal_ref || '—'}</span></td>
                      <td style={td}>{d.client_name || '—'}</td>
                      <td style={td}>{d.company_name}</td>
                      <td style={td}>{d.broker_name || '—'}</td>
                      <td style={td}>
                        <span style={{
                          background: d.invite_status === 'active' ? '#d1fae5' : '#f3f4f6',
                          color: d.invite_status === 'active' ? '#065f46' : '#6b7280',
                          padding: '2px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 600
                        }}>
                          {d.invite_status || 'sent'}
                        </span>
                      </td>
                      <td style={td}>
                        <div style={{ fontSize: '13px' }}>
                          <span style={{ color: '#10b981', fontWeight: 600 }}>{d.approved_docs}</span>
                          <span style={{ color: '#9ca3af' }}> / {d.total_docs}</span>
                          {parseInt(d.rejected_docs) > 0 && <span style={{ color: '#ef4444', marginLeft: '8px' }}>{d.rejected_docs} rejected</span>}
                          {parseInt(d.pending_docs) > 0 && <span style={{ color: '#f59e0b', marginLeft: '8px' }}>{d.pending_docs} pending</span>}
                        </div>
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ flex: 1, background: '#e5e7eb', borderRadius: '999px', height: '8px', overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: '999px', transition: 'width 0.3s' }} />
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: 600, color, width: '36px', textAlign: 'right' }}>{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}

const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' };
const th = { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '12px 16px', fontSize: '14px', color: '#374151', verticalAlign: 'middle' };
const alertRed = { background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' };
